import { openDialog } from '@component/dialog';
import { t } from 'i18next';
import { RtcMediaPermissionStatus, RtcMediaPermissionType } from '@constant/media';
import { BOOL, StreamType, UserInfoItem } from '@tencent/wemeet-websdk-next';

/** @description 判断用户视频是否打开 */
export const isUserVideoOn = (user: UserInfoItem) => user?.up_video_on?.__value === BOOL.true;

/** @description 判断用户音频是否打开 */
export const isUserAudioOn = (user: UserInfoItem) => user?.up_audio_on?.__value === BOOL.true;

/** @description 判断用户屏幕共享是否打开 */
export const isUserShareOn = (user: UserInfoItem) => user?.up_share_on?.__value === BOOL.true;

/** @description 判断用户是否有流媒体数据 */
export const hasStream = (user: UserInfoItem) => !!user?.rtcStream?.stream;

/** @description 判断是否有共享流 */
export const hasScreen = (user: UserInfoItem) =>
  user?.rtcStream?.streamType === StreamType.auxiliary || user?.rtcStream?.streamType === StreamType.localAuxiliary;

/** @description 判断是否开启录制 */
export const openRecord = (user: UserInfoItem) => user?.local_record_on === BOOL.true;

export const requestUserMediaPermission = async (name: RtcMediaPermissionType): Promise<RtcMediaPermissionStatus> => {
  try {
    const constraints = {
      video: false,
      audio: false,
    };
    if (name === RtcMediaPermissionType.Camera) {
      constraints.video = true;
    } else if (name === RtcMediaPermissionType.Microphone) {
      constraints.audio = true;
    }
    await navigator.mediaDevices.getUserMedia(constraints);
    return RtcMediaPermissionStatus.Granted;
  } catch (err: any) {
    if (['NotAllowedError', 'SecurityError'].includes(err.name)) {
      return RtcMediaPermissionStatus.Denied;
    }
    if (['NotFoundError'].includes(err.name)) {
      return RtcMediaPermissionStatus.Prompt;
    }
    if (['TypeError'].includes(err.name)) {
      // 不应该出现这个case！
      throw new TypeError('constraints对象未设置［空］，或者都被设置为false。');
    }
  }
  return RtcMediaPermissionStatus.Prompt;
};

/**
 * @doc 参考：https://github.com/chromium/permissions.request/blob/master/permissions.request.ts
 */
export const queryPermissionStatus = async (name: RtcMediaPermissionType): Promise<RtcMediaPermissionStatus> => {
  if ('permissions' in window.navigator) {
    try {
      // @ts-ignore
      const permissionStatus = await window.navigator.permissions.query({ name });
      // @ts-ignore
      return permissionStatus.state;
    } catch (e) {
      // windows qq浏览器10.3.2 Beta (2928) 兼容性问题
      // Error in v-on handler (Promise/async):
      // "TypeError: Failed to read the 'query' property from 'Permissions':
      // The provided value 'camera' is not a valid enum value of type PermissionName.
      return RtcMediaPermissionStatus.Prompt;
    }
  }
  return await requestUserMediaPermission(name);
};

export const getVideoInputDevices = async (): Promise<number> => {
  if (typeof navigator.mediaDevices === 'object' && typeof navigator.mediaDevices.enumerateDevices === 'function') {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter((item) => item.kind === 'videoinput').length;
  }
  return 0;
};

export const getAudioInputDevices = async (): Promise<number> => {
  if (typeof navigator.mediaDevices === 'object' && typeof navigator.mediaDevices.enumerateDevices === 'function') {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter((item) => item.kind === 'audioinput').length;
  }

  console.log('navigator.mediaDevices call failed');
  return 0;
};

const streamIdIsLoadingMap: Record<string, boolean> = {};

export const setLoadingMapValue = (id: string, flag: boolean) => {
  streamIdIsLoadingMap[id] = flag;
};

export const getLoadingMapValue = (id: string) => streamIdIsLoadingMap[id];

export const handleDeviceErr = (deviceName: 'camera' | 'microphone', err: any) => {
  if (err?.name === 'NotReadableError') {
    openDialog({
      title: t('您的麦克风或摄像头正在被其他应用使用，请停止其他应用后重新加入会议。'),
      okText: t('我知道了'),
    });
  } else if (err?.name === 'NotAllowedError') {
    openDialog({
      title: t('错误'),
      content: t(`请授权访问${deviceName === 'microphone' ? '麦克风' : '摄像头'}设备`),
      okText: t('我知道了'),
    });
  } else {
    console.log(err);
    openDialog({
      title: t('错误'),
      content: t(`操作${deviceName === 'microphone' ? '麦克风' : '摄像头'}超时，请重试`),
      okText: t('我知道了'),
    });
  }
  console.log('error:', err);
};
